import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { operatorRequiresValue } from "../../../Components/filters/operators";

function InternTaskTimeLogList() {
  const navigate = useNavigate();
  const { userData, reporting_manager_interns } = useAuthStore();
  const orgId =
    userData?.organization?.organization_id ??
    userData?.user?.organization_id ??
    userData?.organization_id ??
    null;
  const [searchParams, setSearchParams] = useSearchParams();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const ViewFunction = (obj) => {
    navigate(`/project-task-schedule-timelog/interns/view/${obj?.id}`);
  };

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const currentParams = new URLSearchParams(searchParams);
    let needsUpdate = false;

    if (!currentParams.has("page")) {
      currentParams.set("page", "1");
      needsUpdate = true;
    }
    if (!currentParams.has("pageSize")) {
      currentParams.set("pageSize", "10");
      needsUpdate = true;
    }
    if (!currentParams.has("sortBy")) {
      currentParams.set("sortBy", "log_date");
      needsUpdate = true;
    }
    if (!currentParams.has("sortOrder")) {
      currentParams.set("sortOrder", "desc");
      needsUpdate = true;
    }

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true });
    }

    hasInitializedURL.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!orgId) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Project_task_schedule_timelog_grid_${orgId}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig?.datagrid_default_configuration) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(null);
            }
          } else {
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [orgId]);

  const fetchTimeLogsWithFilters = useCallback(async () => {
    if (!orgId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const filters = [];
      const processedFields = new Set();

      searchParams.forEach((value, key) => {
        if (key.endsWith("_operator") || key.endsWith("_logic") || key.endsWith("_label")) return;

        if (key.startsWith("filter_")) {
          const field = key.replace("filter_", "");
          if (processedFields.has(field)) return;
          processedFields.add(field);

          const operatorKey = `filter_${field}_operator`;
          const operator = searchParams.get(operatorKey) || "contains";

          const requiresValue = operatorRequiresValue(operator);
          if (!requiresValue || value) {
            let filterValue = requiresValue ? value : null;

            if (requiresValue && filterValue) {
              if (operator === "equals" || operator === "not_equals") {
                filterValue = filterValue
                  .split(",")
                  .map((v) => v.trim().toLowerCase());
              } else if (operator === "between" || operator === "not_between") {
                filterValue = filterValue.split(",").map((v) => v.trim());
              }
            }

            filters.push({ field, operator, value: filterValue });
          }
        }
      });

      const search = searchParams.get("search");

      const requestBody = {
        organization_id: orgId,
        person_type_id: 2,
        page: Number.parseInt(searchParams.get("page"), 10) || 1,
        // Backend metadata engine caps `limit` at 100. Clamp pageSize to avoid validation failure.
        pageSize: Math.min(
          Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          100
        ),
        sort: {
          field: searchParams.get("sortBy") || "log_date",
          direction: searchParams.get("sortOrder") || "desc",
        },
        filters,
        ...(search && search.trim() !== "" && { search: search.trim() }),
      };

      // Restrict to reporting manager interns if provided (mirrors filterByReportingManagerInterns)
      if (
        Array.isArray(reporting_manager_interns) &&
        reporting_manager_interns.length > 0
      ) {
        requestBody.organization_person_ids = reporting_manager_interns;
      }

      const response = await fetch(
        `${MAIN_URL}/api/project-management/project-task-schedule-timelog/filtered`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        const normalized = result.data.map((item) => {
          const rawTaskId =
            item.organization_project_task_id ??
            item.organizationProjectTaskId ??
            null;
          const rawOrgPersonId =
            item.organization_person_id ??
            item.organizationPersonId ??
            null;
          const rawProjectId =
            item.organization_project_id ??
            item.organizationProjectId ??
            null;
          const rawTaskTypeId =
            item.organization_project_task_type_id ??
            item.task_type_id ??
            null;

          return {
            ...item,
            id: item.organization_project_task_schedule_time_log_id ?? item.id,

            // FK label fallbacks (different metadata configs may return different keys)
            task:
              item.task ??
              item.organization_project_task ??
              item.task_title ??
              rawTaskId,
            organization_project_task:
              item.organization_project_task ??
              item.task ??
              item.task_title ??
              rawTaskId,
            employee:
              item.employee ??
              item.organization_person ??
              item.full_name ??
              rawOrgPersonId,
            organization_person:
              item.organization_person ??
              item.employee ??
              item.full_name ??
              rawOrgPersonId,
            project_name:
              item.project_name ??
              item.organization_project ??
              rawProjectId,
            organization_project:
              item.organization_project ??
              item.project_name ??
              rawProjectId,
            task_type:
              item.task_type ??
              item.task_type_name ??
              rawTaskTypeId,

            // Legacy typo passthrough: old `index` mode=1/3 spelled it `scheudle_date`.
            // Saved grid configs may still reference that key, so keep it populated.
            scheudle_date:
              item.scheudle_date ?? item.scheduled_date ?? null,
            scheduled_date:
              item.scheduled_date ?? item.scheudle_date ?? null,

            // Back-compat for saved datagrid columns that still point to raw FK keys.
            organization_project_task_id_raw: rawTaskId,
            organization_person_id_raw: rawOrgPersonId,
            organization_project_id_raw: rawProjectId,
            organization_project_task_id:
              item.organization_project_task_id_label ??
              item.task ??
              item.organization_project_task ??
              item.task_title ??
              rawTaskId,
            organization_person_id:
              item.organization_person_id_label ??
              item.employee ??
              item.organization_person ??
              item.full_name ??
              rawOrgPersonId,
            organization_project_id:
              item.organization_project_id_label ??
              item.project_name ??
              item.organization_project ??
              rawProjectId,
          };
        });
        setLogs(normalized);

        if (result.pagination) {
          const pageSize =
            result.pagination.pageSize || result.pagination.limit || 10;
          const requestedPage =
            Number.parseInt(searchParams.get("page"), 10) || 1;
          setPaginationData({
            page: result.pagination.page || requestedPage,
            pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          });
        } else {
          setPaginationData({
            page: 1,
            pageSize: 10,
            total: normalized.length,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          });
        }
      } else {
        setLogs([]);
        setPaginationData({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        });
        toast.error(
          result.message || result.error || "Failed to fetch task time logs"
        );
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Error fetching task time logs:", err);
      toast.error("Failed to fetch task time logs");
      setLogs([]);
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, reporting_manager_interns, searchParams]);

  useEffect(() => {
    fetchTimeLogsWithFilters();
  }, [fetchTimeLogsWithFilters]);

  return (
    <>
      <Layout4
        heading="Intern Task Schedule TimeLogs"
        Route="/project-task-schedule-timelog/interns"
        btnName="Add Intern Task Schedule Timelog"
        showAddButton={true}
        add_action="ADD_INTERN_TASK_SCHEDULE_TIMELOGS"
      />

      {loading || loadingConfig ? (
        <div style={{ padding: 16 }}>Loading...</div>
      ) : (
        <TableDataGeneric
          viewFunction={(obj) => ViewFunction(obj)}
          gridKey="Project_task_schedule_timelog_grid"
          tableName="organization_project_task_schedule_time_logs"
          filterModule="organization_project_task_schedule_time_logs_intern"
          primaryKey="id"
          heading="organization_project_task_schedule_time_logs"
          data={logs}
          sortname={"log_date"}
          showActions={true}
          Route="/project-task-schedule-timelog/interns"
          token={localStorage.getItem("token")}
          configss={configColumns}
          edit_delete_action={[
            "EDIT_INTERN_TASK_SCHEDULE_TIMELOGS||EDIT_INTERN_TASK_SCHEDULE_TIEMLOGS",
            "DELETE_INTERN_TASK_SCHEDULE_TIMELOGS",
          ]}
          paginationData={paginationData}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default InternTaskTimeLogList;
